import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import withStyles, {
  WithStyles,
  StyleRulesCallback
} from '@material-ui/core/styles/withStyles';

import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import {
  BaseMap,
  DocumentGrid,
  GeotiffOverlay,
  RasterMapReference,
  NdviRange
} from '../components';

import { GeotiffRender } from '../utils/renderGeotiffRaster';

import {
  FieldProvider,
  LayoutToolsProvider,
  LayoutTools,
  FarmLibraryProvider
} from '../providers';

import AnalyzeYieldProvider, {
  YieldAnalysisParams,
  YieldAnalysisResult
} from '../providers/AnalyzeYieldProvider';

import UpsertDocumentProvider, {
  UpsertDocumentFn,
  SaveYieldAnalysisParams
} from '../providers/UpsertDocumentProvider';

import {
  Paper,
  Typography,
  Button,
  Snackbar,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Collapse,
  Checkbox,
  IconButton
} from '@material-ui/core';

import { DatePicker } from 'material-ui-pickers';

import BackIcon from '@material-ui/icons/ArrowBack';
import NdviIcon from '@material-ui/icons/Photo';
import TodayIcon from '@material-ui/icons/Today';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';

import { Stepper, Step, StepLabel, StepContent } from '@material-ui/core';
import SidePanel from './SidePanel';
import { calculateFieldViewport } from '../utils';

interface Renders {
  layout: LayoutTools;
  loading: boolean;
  field: Field | null;
  farm: Farm | null;
  layers: RasterDocument[] | null;
  layersFetchMore: () => void;
  execute: (params: YieldAnalysisParams) => void;
  save: UpsertDocumentFn;
  processing: boolean;
  isBlankSlate: boolean;
}

interface State {
  activeStep: number;
  selection: Record<string, boolean>;
  result: YieldAnalysisResult | null;
  criticalMonthsOpen: boolean;
  criticalMonths: number[];
  dateMenuOpen: boolean;
  capturedSince: number | null;
  capturedUntil: number | null;
  ndviRangeOpen: boolean;
  ndviRangeValue: [number, number] | undefined;
  saving: boolean;
  outputRender: GeotiffRender | null;
}

const styles: StyleRulesCallback = theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    height: '100vh'
  },
  stepper: {
    marginTop: theme.spacing.unit * 2
  },
  content: {
    padding: theme.spacing.unit * 3,
    flex: 1,
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'center'
  },
  contentCentered: {
    padding: theme.spacing.unit * 3,
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  map: {
    flex: 1
  }
});

type Props = WithStyles & RouteComponentProps<any>;

function mapSelectionToProcessParams(
  selectedIDs: string[],
  allLayers: RasterDocument[]
): YieldAnalysisParams {
  const sourceRasters = allLayers
    .filter(doc => selectedIDs.includes(doc.id))
    .map(doc => doc.rasterUrl);
  return { sourceRasters };
}

class YieldAnalyisis extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      activeStep: 0,
      selection: {},
      result: null,
      criticalMonthsOpen: false,
      criticalMonths: [],
      dateMenuOpen: false,
      capturedSince: null,
      capturedUntil: null,
      ndviRangeOpen: false,
      ndviRangeValue: undefined,
      saving: false,
      outputRender: null
    };
  }

  handleExecuteClick = async (renders: Renders) => {
    const selectedIDs = this.getSelectedIDs();
    const params = mapSelectionToProcessParams(
      selectedIDs,
      renders.layers as RasterDocument[]
    );
    await this.setState({ activeStep: 1 });
    renders.execute(params);
  };

  handleProcessCompleted = (result: YieldAnalysisResult) => {
    this.setState({ activeStep: 2, result });
  };

  handleOutputRender = (output: GeotiffRender) => {
    this.setState({ outputRender: output });
  };

  handleProcessError = (layers: RasterDocument[] | null, error: any) => {
    console.error(error);
    alert(error);
  };

  handleSave = (renders: Renders) => {
    const { result, outputRender } = this.state;
    const field = renders.field as Field;
    const farm = renders.farm as Farm;
    if (result && outputRender) {
      this.setState({ saving: true });
      renders.save({
        kind: 'yield-analysis',
        farmId: farm.id,
        fieldId: field.id,
        rasterUrl: result.rasterUrl,
        vectorUrl: result.vectorUrl,
        thumbnailUrl: outputRender.dataUrl,
        documentIds: this.getSelectedIDs()
      } as SaveYieldAnalysisParams);
    }
  };

  handleSaveCompleted = () => {
    this.setState({ saving: false });
  };

  getSelectedIDs() {
    const { selection } = this.state;
    return Object.entries(selection)
      .filter(e => e['1'])
      .map(e => e['0']);
  }

  renderLoading(message?: string | null) {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={true}
        message={
          <>
            <CircularProgress color="secondary" size={25} />
            <span>{message || 'cargando'}</span>
          </>
        }
      />
    );
  }

  renderResultMap(renders: Renders) {
    const { classes } = this.props;
    const { result, outputRender } = this.state;
    const field = renders.field as Field;
    const viewport = calculateFieldViewport(field);

    if (!result) {
      return;
    }

    return (
      <BaseMap className={classes.map} viewport={viewport}>
        <GeotiffOverlay
          id={result.rasterUrl}
          geotiffType="grayscale"
          geotiffUrl={result.rasterUrl}
          inputDomain={[1, 255]}
          outputDomain={[0, 200]}
          customBins={null}
          nodata={0}
          geometry={field.geometry}
          onRender={output => this.handleOutputRender(output)}
        />
        <RasterMapReference
          open={true}
          render={outputRender}
          totalArea={field.area}
          fractionDigits={0}
          suffix="%"
        />
      </BaseMap>
    );
  }

  renderBlankSlate(renders: Renders) {
    const { classes } = this.props;
    return (
      <Paper square={true} elevation={1} className={classes.contentCentered}>
        <Typography variant="title" color="textSecondary">
          No hay capas disponibles para realizar el análisis.
        </Typography>
      </Paper>
    );
  }

  renderSupplyGrid(renders: Renders) {
    const { classes } = this.props;
    const { selection } = this.state;

    if (renders.isBlankSlate) {
      return this.renderBlankSlate(renders);
    }

    return (
      <Paper square={true} elevation={1} className={classes.content}>
        <DocumentGrid
          selectable={true}
          documents={renders.layers}
          onFetchMore={renders.layersFetchMore}
          loading={false}
          selectedIDs={this.getSelectedIDs()}
          onSelect={(doc, checked) => {
            this.setState({ selection: { ...selection, [doc.id]: checked } });
          }}
        />
      </Paper>
    );
  }

  renderBigLoading() {
    const { classes } = this.props;
    return (
      <Paper square={true} elevation={1} className={classes.contentCentered}>
        <CircularProgress size={150} />
        <Typography variant="title" color="textSecondary">
          Ejecutando Agro-ritmo
        </Typography>
      </Paper>
    );
  }

  handleToggleMonth(month: number) {
    if (this.state.criticalMonths.find(m => m === month)) {
      this.setState({
        criticalMonths: this.state.criticalMonths.filter(m => m !== month)
      });
    } else {
      this.setState({
        criticalMonths: [...this.state.criticalMonths, month]
      });
    }
  }

  renderMonthCheckbox(monthNumber: number, monthName: string) {
    return (
      <ListItem key={monthNumber} dense={true}>
        <ListItemText primary={monthName} />
        <ListItemSecondaryAction>
          <Checkbox
            checked={this.state.criticalMonths.indexOf(monthNumber) > -1}
            onChange={() => this.handleToggleMonth(monthNumber)}
          />
        </ListItemSecondaryAction>
      </ListItem>
    );
  }

  renderFilters() {
    return (
      <List>
        <ListItem
          button={true}
          onClick={() =>
            this.setState({
              dateMenuOpen: !this.state.dateMenuOpen
            })
          }
        >
          <ListItemIcon>
            <TodayIcon />
          </ListItemIcon>
          <ListItemText primary="Filtrar por Fecha" />
          {this.state.dateMenuOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={this.state.dateMenuOpen}>
          <List style={{ paddingBottom: 16 }}>
            <ListItem style={{ paddingTop: 0, paddingBottom: 0 }}>
              <DatePicker
                fullWidth={true}
                label="Desde"
                clearable={true}
                disableFuture={true}
                value={this.state.capturedSince}
                onChange={d => this.setState({ capturedSince: d })}
                maxDate={this.state.capturedUntil || undefined}
                clearLabel="Limpiar"
                labelFunc={date => (date ? date.format('DD/MM/YYYY') : '')}
                margin="dense"
              />
            </ListItem>
            <ListItem style={{ paddingTop: 0, paddingBottom: 0 }}>
              <DatePicker
                fullWidth={true}
                label="Hasta"
                clearable={true}
                disableFuture={true}
                value={this.state.capturedUntil}
                onChange={d => this.setState({ capturedUntil: d })}
                minDate={this.state.capturedSince || undefined}
                clearLabel="Limpiar"
                labelFunc={date => (date ? date.format('DD/MM/YYYY') : '')}
                margin="dense"
              />
            </ListItem>
          </List>
        </Collapse>

        <Divider />

        <ListItem
          button={true}
          onClick={() =>
            this.setState({
              criticalMonthsOpen: !this.state.criticalMonthsOpen
            })
          }
        >
          <ListItemIcon>
            <TodayIcon />
          </ListItemIcon>
          <ListItemText primary="Meses críticos" />
          {this.state.criticalMonthsOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={this.state.criticalMonthsOpen}>
          <List style={{ maxHeight: 250, overflowY: 'scroll' }}>
            {this.renderMonthCheckbox(1, 'Enero')}
            {this.renderMonthCheckbox(2, 'Febrero')}
            {this.renderMonthCheckbox(3, 'Marzo')}
            {this.renderMonthCheckbox(4, 'Abril')}
            {this.renderMonthCheckbox(5, 'Mayo')}
            {this.renderMonthCheckbox(6, 'Junio')}
            {this.renderMonthCheckbox(7, 'Julio')}
            {this.renderMonthCheckbox(8, 'Agosto')}
            {this.renderMonthCheckbox(9, 'Septiembre')}
            {this.renderMonthCheckbox(10, 'Octubre')}
            {this.renderMonthCheckbox(11, 'Noviembre')}
            {this.renderMonthCheckbox(12, 'Diciembre')}
          </List>
        </Collapse>

        <Divider />

        <ListItem
          button={true}
          onClick={() =>
            this.setState({
              ndviRangeOpen: !this.state.ndviRangeOpen
            })
          }
        >
          <ListItemIcon>
            <NdviIcon />
          </ListItemIcon>
          <ListItemText primary="Rango de NDVI" />
          {this.state.ndviRangeOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={this.state.ndviRangeOpen}>
          <List style={{ paddingBottom: 16, paddingTop: 16 }}>
            <ListItem
              style={{
                paddingTop: 0,
                paddingBottom: 0,
                flexDirection: 'column'
              }}
            >
              <NdviRange
                onChange={ndviRangeValue => this.setState({ ndviRangeValue })}
                value={this.state.ndviRangeValue}
              />
            </ListItem>
          </List>
        </Collapse>
      </List>
    );
  }

  composeProviders(children: (r: Renders) => React.ReactNode) {
    const {
      match: {
        params: { fieldId }
      }
    } = this.props;

    return (
      <LayoutToolsProvider>
        {layout => (
          <FieldProvider
            fieldId={fieldId}
            includeFarm={true}
            includeLayers={false}
          >
            {fieldRenders => (
              <FarmLibraryProvider
                farmId={
                  (fieldRenders.farm && fieldRenders.farm.id) || undefined
                }
                first={9}
                filter={{
                  fieldId: fieldId,
                  kind: ['raster-ndvi'],
                  criticalMonths: this.state.criticalMonths,
                  capturedSince: this.state.capturedSince,
                  capturedUntil: this.state.capturedUntil,
                  minMeanNdvi: this.state.ndviRangeValue && this.state.ndviRangeValue[0],
                  maxMeanNdvi: this.state.ndviRangeValue && this.state.ndviRangeValue[1],
                }}
              >
                {libResult => (
                  <AnalyzeYieldProvider
                    onCompleted={this.handleProcessCompleted}
                    onError={error =>
                      this.handleProcessError(fieldRenders.layers, error)
                    }
                  >
                    {analyzeRenders => (
                      <UpsertDocumentProvider
                        onCompleted={this.handleSaveCompleted}
                      >
                        {(save: UpsertDocumentFn) =>
                          children({
                            layout,
                            loading: fieldRenders.loading || libResult.loading,
                            field: fieldRenders.field,
                            farm: fieldRenders.farm,
                            layers: libResult.documents as RasterDocument[],
                            layersFetchMore: libResult.fetchMore,
                            execute: analyzeRenders.execute,
                            save: save,
                            processing: analyzeRenders.processing,
                            isBlankSlate:
                              !fieldRenders.loading &&
                              !libResult.loading &&
                              libResult.documents != null &&
                              !libResult.documents.length
                          })
                        }
                      </UpsertDocumentProvider>
                    )}
                  </AnalyzeYieldProvider>
                )}
              </FarmLibraryProvider>
            )}
          </FieldProvider>
        )}
      </LayoutToolsProvider>
    );
  }

  renderStepper(renders: Renders) {
    const { classes } = this.props;
    const selectedIDs = this.getSelectedIDs();
    return (
      <Stepper
        activeStep={this.state.activeStep}
        orientation="vertical"
        className={classes.stepper}
      >
        <Step key={0}>
          <StepLabel>Seleccionar Insumos</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Elija de su biblioteca aquellos insumos (raster de NDVI, mapas de
              rinde, etc) que sean representativos de los ambientes de su lote
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              disabled={!selectedIDs.length}
              onClick={() => this.handleExecuteClick(renders)}
            >
              Ejecutar
            </Button>
            <Typography color={selectedIDs.length ? 'textSecondary' : 'error'}>
              Ha seleccionado {selectedIDs.length} insumo/s
            </Typography>
          </StepContent>
        </Step>
        <Step key={1}>
          <StepLabel>Procesar Insumos</StepLabel>
          <StepContent>
            <Typography variant="subheading">
              El servidor esta normalizando los valores de cada insumo,
              calculando el promedio de cada punto y definiendo ambientes
              homogeneos dentro del lote.
            </Typography>
          </StepContent>
        </Step>
        <Step key={2}>
          <StepLabel>Validar Resultado</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Este es el resultado del analisis. Si esta conforme con los
              ambientes generados, puede guardar el analisis en su biblioteca.
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              style={{ marginBottom: 20 }}
              onClick={() => this.handleSave(renders)}
              disabled={this.state.saving}
            >
              Guardar en biblioteca
            </Button>
            <Button
              fullWidth={true}
              variant="raised"
              onClick={() => this.setState({ activeStep: 0 })}
            >
              Volver a comenzar
            </Button>
          </StepContent>
        </Step>
      </Stepper>
    );
  }

  render() {
    const { classes } = this.props;

    const { activeStep, selection, saving } = this.state;

    console.log(selection);

    return this.composeProviders(renders => (
      <main className={classes.root}>
        {renders.loading && this.renderLoading('cargando lote')}
        {saving && this.renderLoading('guardando análisis')}

        <SidePanel>
          <div style={{display: 'flex'}}>
            <div style={{flex: 0, marginRight: 16, alignItems: 'center'}}>
              <IconButton onClick={() => this.props.history.goBack()}>
                <BackIcon />
              </IconButton>
            </div>
            <div style={{flex: 1}}>
              <Typography variant="subheading">
                Analisis de Producitvidad
              </Typography>
              <Typography variant="headline" style={{ marginBottom: 16 }}>
                {(renders.field && renders.field.name) || 'Cargando Lote...'}
              </Typography>
            </div>
          </div>
          {activeStep === 0 && this.renderFilters()}
          {this.renderStepper(renders)}
        </SidePanel>

        {activeStep === 0 && this.renderSupplyGrid(renders)}
        {activeStep === 1 && this.renderBigLoading()}
        {activeStep === 2 && this.renderResultMap(renders)}
      </main>
    ));
  }
}

export default withStyles(styles)(withRouter(YieldAnalyisis));



// WEBPACK FOOTER //
// ./src/containers/YieldAnalysis.tsx